class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', this.onOfferClick.bind(this));
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.positionExclusiveLines();
  }

  async onOfferClick(event) {
    const button = event.target.closest('[data-cart-offer-action]');
    if (!button || button.disabled) return;

    const offer = button.closest('.cart-offer');
    const errorElement = offer?.querySelector('[data-cart-offer-error]');
    const spinner = button.querySelector('.loading__spinner');
    const action = button.dataset.cartOfferAction;
    const sections = this.getSectionsToRender().map((section) => section.id);
    let endpoint = routes.cart_add_url;
    let body;

    if (action === 'add-addon') {
      body = {
        items: [{
          id: Number(button.dataset.variantId),
          quantity: 1,
          properties: {
            _dobel_new_subscriber_exclusive: 'true',
            _dobel_exclusive_source_product_id: button.dataset.sourceProductId,
          },
        }],
        sections,
        sections_url: window.location.pathname,
      };
    } else if (action === 'complete-four') {
      endpoint = routes.cart_change_url;
      body = {
        id: button.dataset.lineKey,
        quantity: Number(button.dataset.lineQuantity) + Number(button.dataset.missingQuantity),
        sections,
        sections_url: window.location.pathname,
      };
    } else {
      return;
    }

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.classList.add('loading');
    spinner?.classList.remove('hidden');
    if (errorElement) {
      errorElement.hidden = true;
      errorElement.textContent = '';
    }
    this.setActiveElement(button);

    try {
      const response = await fetch(endpoint, { ...fetchConfig(), body: JSON.stringify(body) });
      const state = await response.json();
      if (!response.ok || state.status) {
        throw new Error(state.description || state.message || window.cartStrings.error);
      }

      await publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-offer',
        productVariantId: button.dataset.variantId,
        cartData: state,
      });
      this.renderContents(state);
    } catch (error) {
      if (errorElement) {
        errorElement.textContent = error.message || window.cartStrings.error;
        errorElement.hidden = false;
      }
    } finally {
      if (button.isConnected) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.classList.remove('loading');
        spinner?.classList.add('hidden');
      }
    }
  }

  positionExclusiveLines() {
    this.querySelectorAll('[data-exclusive-source-product-id]').forEach((exclusiveRow) => {
      const sourceProductId = exclusiveRow.dataset.exclusiveSourceProductId;
      if (!sourceProductId) return;

      const sourceRow = this.querySelector(
        `[data-cart-product-id="${sourceProductId}"]:not([data-exclusive-source-product-id])`,
      );
      sourceRow?.insertAdjacentElement('afterend', exclusiveRow);
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.setAttribute('aria-expanded', 'false');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    this.positionExclusiveLines();
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (cartLink) cartLink.setAttribute('aria-expanded', 'true');
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (cartLink) cartLink.setAttribute('aria-expanded', 'false');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.positionExclusiveLines();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
